import { useTranslation } from 'react-i18next';
import { ALLERGIES, toTranslationKey } from '../../types/RecipeEnums';
import { ALLERGEN_SUBTYPES, AllergenDraft, canReviewAllergens, changeAllergen, getUnmappedLegacyAllergens } from '../../types/RecipeAllergens';

interface Props {
  value: AllergenDraft;
  onChange: (value: AllergenDraft) => void;
  disabled?: boolean;
}

export function LegacyAllergenWarnings({ allergies }: { allergies?: readonly string[] }) {
  const { t } = useTranslation();
  const unmapped = getUnmappedLegacyAllergens(allergies);
  if (!unmapped.length) return null;
  return (
    <div role="note" className="mb-3 space-y-2 text-sm text-amber-800 dark:text-amber-300">
      <p>{t('recipeAllergens.unmappedLegacyWarnings')}</p>
      <ul className="list-disc pl-5 break-words">
        {unmapped.map(allergy => <li key={allergy}>{allergy}</li>)}
      </ul>
    </div>
  );
}

export default function AllergenDeclarationEditor({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-3 text-sm text-zinc-900 dark:text-zinc-100">
      <legend className="font-semibold mb-2">{t('recipeAllergens.declaration')}</legend>
      <p className="text-zinc-600 dark:text-zinc-400">{t('recipeAllergens.recipeOnly')}</p>
      {ALLERGIES.map(group => {
        const declaration = value.groups[group] ?? { presence: 'unknown' as const };
        const subtypeOptions = ALLERGEN_SUBTYPES[group];
        return (
          <div key={group} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 space-y-2">
            <label className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
              <span className="font-medium">{t(`allergyKeys.${toTranslationKey(group)}`)}</span>
              <select value={declaration.presence} onChange={event => onChange(changeAllergen(value, group, {
                presence: event.target.value as typeof declaration.presence,
              }))} className="w-full sm:w-auto min-w-0 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-2">
                <option value="unknown">{t('recipeAllergens.presence.unknown')}</option>
                <option value="contains">{t('recipeAllergens.presence.contains')}</option>
                <option value="absent">{t('recipeAllergens.presence.absent')}</option>
              </select>
            </label>
            {declaration.presence === 'contains' && subtypeOptions && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {subtypeOptions.map(subtype => (
                    <label key={subtype} className="flex items-center gap-2 py-1">
                      <input type="checkbox" checked={declaration.subtypes?.includes(subtype) ?? false} onChange={event => {
                        const selected = event.target.checked
                          ? [...(declaration.subtypes ?? []), subtype]
                          : (declaration.subtypes ?? []).filter(item => item !== subtype);
                        onChange(changeAllergen(value, group, {
                          ...declaration, subtypes: subtypeOptions.filter(item => selected.includes(item)),
                          subtypesComplete: selected.length > 0 && declaration.subtypesComplete === true,
                        }));
                      }} />
                      {t(`recipeAllergens.subtypes.${subtype}`)}
                    </label>
                  ))}
                </div>
                <label className="flex items-start gap-2 py-1">
                  <input type="checkbox" className="mt-1" checked={declaration.subtypesComplete !== true || !declaration.subtypes?.length}
                    disabled={!declaration.subtypes?.length} onChange={event => onChange(changeAllergen(value, group, {
                      ...declaration, subtypesComplete: !event.target.checked,
                    }))} />
                  {t('recipeAllergens.incomplete')}
                </label>
              </div>
            )}
          </div>
        );
      })}
      <p>{t('recipeAllergens.reviewStatus')}: {t(`recipeAllergens.review.${value.review.status}`)}</p>
      <label className="flex items-start gap-2 py-2">
        <input type="checkbox" className="mt-1" checked={value.review.status === 'reviewed'} disabled={!canReviewAllergens(value)}
          onChange={event => onChange({ ...value, review: { status: event.target.checked ? 'reviewed' : 'unreviewed' } })} />
        <span>{t('recipeAllergens.markReviewed')}<span className="block text-zinc-600 dark:text-zinc-400">{t('recipeAllergens.reviewRequirement')}</span></span>
      </label>
    </fieldset>
  );
}

export function AllergenDeclarationSummary({ value }: { value: AllergenDraft }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 space-y-2 text-sm text-zinc-700 dark:text-zinc-300">
      <p className="font-medium">{t('recipeAllergens.reviewStatus')}: {t(`recipeAllergens.review.${value.review.status}`)}</p>
      {value.review.reviewedAt && <p>{t('recipeAllergens.reviewedAt')}: {new Date(value.review.reviewedAt).toLocaleString()}</p>}
      <dl className="space-y-2">
        {ALLERGIES.map(group => {
          const declaration = value.groups[group];
          return (
            <div key={group}>
              <dt className="font-medium">{t(`allergyKeys.${toTranslationKey(group)}`)}</dt>
              <dd>{t(`recipeAllergens.presence.${declaration?.presence ?? 'unknown'}`)}
                {declaration?.presence === 'contains' && ALLERGEN_SUBTYPES[group] && <>
                  {': '}{declaration.subtypes?.map(subtype => t(`recipeAllergens.subtypes.${subtype}`)).join(', ')}
                  {(declaration.subtypesComplete !== true || !declaration.subtypes?.length) && <span> {t('recipeAllergens.incomplete')}</span>}
                </>}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
